import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { OrgComponentProps, OrgSettingsTabType, OrgSettingsTypeComponentMatchParams } from '../../typescriptHelpers';
import { antGrey, pageContentStyle, runwayColors, titleStyle } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Layout, { Content } from 'antd/lib/layout/layout';
import { Event } from '../../utils/analytics';
import Title from 'antd/lib/typography/Title';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Button from 'antd/lib/button';
import {
    LockOutlined,
    TeamOutlined,
    CloudOutlined,
    ApiOutlined,
    AppstoreAddOutlined,
    ApartmentOutlined,
} from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import {
    selectOrg,
    selectIsStatelessServer,
    selectUserIsRunwayAdmin,
    selectOrgSubscriptionPlanText,
} from '../../selectors/selectors';
import ChangeOrgModal from '../userSettings/ChangeOrgModal';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { trackOrgRouteEvent } from '../../actions/trackEvent';
import Tabs from 'antd/lib/tabs';
import WebhooksSettings from '../webhooks/WebhooksSettings';
import OrgSettingsTeam from './OrgSettingsTeam';
import { createOrgSettingsRoute } from '../../helpers/routeHelpers';
import ApiSettings from './ApiSettings';
import { SsoSamlSettings } from './SsoSamlSettings';
import { IntegrationProviderSettings } from './IntegrationProviderSettings';
import { globalMessage } from '../../utils/globalMessage';
import { useUpdated } from '@/utils/hooks/helpers';
import { OrgChecklistItemTemplates } from './OrgChecklistItems';

const OrgSettings: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const history = useHistory();
    const params = useParams<OrgSettingsTypeComponentMatchParams>();
    const [changeOrgModalVisible, setChangeOrgModalVisible] = React.useState(false);

    const dispatch = useDispatch();

    const currentSubscriptionPlanText = useSelector((state: RootState) =>
        selectOrgSubscriptionPlanText(state, props.orgId),
    );
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));
    const isStatelessServer = useSelector((state: RootState) => selectIsStatelessServer(state));
    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));

    const settingUpdated = useUpdated(OrgSettingsUpdatable.MergeAccountsSetting);

    const showChangeOrg = isStatelessServer && isRunwayAdmin;

    React.useEffect(() => {
        if (settingUpdated) {
            globalMessage.success('Organization setting updated!');
        }
    }, [settingUpdated]);

    // track amplitude
    React.useEffect(() => {
        dispatch(trackOrgRouteEvent({ orgId: props.orgId, eventName: Event.ViewOrgSettings }));
    }, [props.orgId]);

    const activeTab = params.orgSettingsTabType ?? OrgSettingsTabType.Team;

    return (
        <Layout
            style={{
                ...pageContentStyle,
                position: 'relative',
                paddingLeft: 32,
                paddingRight: 32,
                paddingBottom: 0,
            }}
        >
            <Space direction="vertical" style={{ marginBottom: 24 }}>
                <Space align="center">
                    <Title style={{ fontSize: 24, marginBottom: 0 }}>{org.name}</Title>
                    {currentSubscriptionPlanText && (
                        <Tag
                            style={{
                                borderRadius: 30,
                                fontWeight: 600,
                                fontSize: 11,
                                borderStyle: 'none',
                                paddingLeft: 8,
                                paddingRight: 8,
                                paddingTop: 2,
                                paddingBottom: 2,
                            }}
                            color={antGrey.grey8}
                        >
                            {currentSubscriptionPlanText}
                        </Tag>
                    )}
                    {showChangeOrg && (
                        <Button type="link" onClick={() => setChangeOrgModalVisible(true)}>
                            Switch organizations
                        </Button>
                    )}
                </Space>
                <Text style={titleStyle}>Organization settings</Text>
            </Space>
            <Tabs
                animated={false}
                activeKey={activeTab}
                onChange={(activeKey) => {
                    const tabType = activeKey as OrgSettingsTabType;
                    const route = createOrgSettingsRoute(params.orgIdParam, tabType);

                    history.push(route);
                }}
                style={{ height: '100%' }}
                className="org-settings-tabs"
            >
                <Tabs.TabPane
                    tab={
                        <Space size={0} style={{ paddingRight: 8 }}>
                            <TeamOutlined
                                style={{
                                    color: activeTab === OrgSettingsTabType.Team ? runwayColors.primary : antGrey.grey6,
                                }}
                            />
                            <Text
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.Team ?
                                            runwayColors.primary
                                        :   runwayColors.darkTextGrey,
                                }}
                            >
                                Team
                            </Text>
                        </Space>
                    }
                    key="team"
                >
                    <OrgSettingsTeam orgId={props.orgId} />
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Space size={0} style={{ paddingRight: 8 }}>
                            <ApartmentOutlined
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.Checklists ?
                                            runwayColors.primary
                                        :   antGrey.grey6,
                                }}
                            />
                            <Text
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.Checklists ?
                                            runwayColors.primary
                                        :   runwayColors.darkTextGrey,
                                }}
                            >
                                Checklist items
                            </Text>
                        </Space>
                    }
                    key={OrgSettingsTabType.Checklists}
                >
                    <Content>
                        <OrgChecklistItemTemplates orgId={props.orgId} />
                    </Content>
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Space size={0} style={{ paddingRight: 8 }}>
                            <CloudOutlined
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.Webhooks ?
                                            runwayColors.primary
                                        :   antGrey.grey6,
                                }}
                            />
                            <Text
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.Webhooks ?
                                            runwayColors.primary
                                        :   runwayColors.darkTextGrey,
                                }}
                            >
                                Webhooks
                            </Text>
                        </Space>
                    }
                    key={OrgSettingsTabType.Webhooks}
                >
                    <Content>
                        <WebhooksSettings orgId={props.orgId} />
                    </Content>
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Space size={0} style={{ paddingRight: 8 }}>
                            <ApiOutlined
                                style={{
                                    color: activeTab === OrgSettingsTabType.Api ? runwayColors.primary : antGrey.grey6,
                                }}
                            />
                            <Text
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.Api ?
                                            runwayColors.primary
                                        :   runwayColors.darkTextGrey,
                                }}
                            >
                                API
                            </Text>
                        </Space>
                    }
                    key="api"
                >
                    <ApiSettings orgId={props.orgId} />
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Space size={0} style={{ paddingRight: 8 }}>
                            <LockOutlined
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.SsoSaml ? runwayColors.primary : antGrey.grey6,
                                }}
                            />
                            <Text
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.SsoSaml ?
                                            runwayColors.primary
                                        :   runwayColors.darkTextGrey,
                                }}
                            >
                                SSO/SAML
                            </Text>
                        </Space>
                    }
                    key="sso-saml"
                >
                    <SsoSamlSettings orgId={props.orgId} />
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Space size={0} style={{ paddingRight: 8 }}>
                            <AppstoreAddOutlined
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.Integration ?
                                            runwayColors.primary
                                        :   antGrey.grey6,
                                }}
                            />
                            <Text
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.Integration ?
                                            runwayColors.primary
                                        :   runwayColors.darkTextGrey,
                                }}
                            >
                                Integrations
                            </Text>
                        </Space>
                    }
                    key="integration"
                >
                    <IntegrationProviderSettings orgId={props.orgId} />
                </Tabs.TabPane>
            </Tabs>
            {showChangeOrg && (
                <ChangeOrgModal
                    open={changeOrgModalVisible}
                    currentOrgId={props.orgId}
                    onCancel={() => setChangeOrgModalVisible(false)}
                    onSuccess={() => setChangeOrgModalVisible(false)}
                />
            )}
        </Layout>
    );
};

export default OrgSettings;
